import posthog from 'posthog-js';
import type { PostHogConfig } from 'posthog-js';

// posthog-js is a browser-only singleton. Importing statically (matching
// components/posthog-provider.tsx) gives every call site the same instance and
// eliminates a race where the previous dynamic import left `posthog` null for
// the first few hundred ms after mount, silently dropping any `trackEvent` /
// `captureException` calls that fired in that window (suspected contributor to
// the low post_created / reaction_added / notifications_viewed volume in LB-184
// / LB-190 / LB-172).

export const initPostHog = () => {
  // Only initialize PostHog in production
  if (process.env.NODE_ENV !== 'production') {
    return;
  }

  if (typeof window !== 'undefined' && process.env.NEXT_PUBLIC_POSTHOG_KEY && posthog) {
    // Don't initialize if already initialized
    if (posthog.__loaded) return;

    const config: Partial<PostHogConfig> = {
      // Ingest through our reverse proxy (cf.trycommons.com) so ad-blockers don't
      // silently drop events. ui_host keeps SDK-generated links (toolbar, session
      // replay, "view in PostHog") pointing at the real app rather than the proxy.
      api_host: process.env.NEXT_PUBLIC_POSTHOG_HOST || 'https://cf.trycommons.com',
      ui_host: 'https://us.posthog.com',
      // Capture pageviews automatically
      capture_pageview: false, // We'll manually track in PostHogProvider
      // Capture page leaves
      capture_pageleave: true,
      // Auto-capture interactions
      autocapture: {
        dom_event_allowlist: ['click', 'submit', 'change'],
        element_allowlist: ['button', 'input', 'select', 'textarea', 'a'],
      },
      // Enable exception autocapture for error tracking
      capture_exceptions: true,
      // Drop user-cancellation FirebaseErrors that aren't real errors (LB-182).
      // signInWithGoogle re-throws these so the UI can recover; without this
      // hook, capture_exceptions autocapture still flags them in Error Tracking.
      before_send: (event) => {
        if (event?.event === '$exception') {
          const list = (event.properties as Record<string, unknown> | undefined)
            ?.$exception_list as Array<Record<string, unknown>> | undefined;
          const first = list?.[0];
          const value = String(first?.value || '');
          const type = String(first?.type || '');
          if (
            type === 'FirebaseError' &&
            /auth\/(popup-closed-by-user|cancelled-popup-request|user-cancelled)/.test(value)
          ) {
            return null;
          }
        }
        return event;
      },
      // Session replay - requires enabling in PostHog Project Settings
      disable_session_recording: false,
      session_recording: {
        // Mask all text inputs by default for privacy
        maskAllInputs: true,
        // Mask text content that might contain sensitive data
        maskTextSelector: '[data-ph-mask]',
      },
      // Privacy settings
      persistence: 'localStorage',
      cross_subdomain_cookie: false,
      // Disable debug logging in production
      debug: false,
      // Disable PostHog surveys — we use our own feedback components
      disable_surveys: true,
    };

    posthog.init(
      process.env.NEXT_PUBLIC_POSTHOG_KEY,
      config
    );
  }
};

// Helper functions for custom events
export const trackEvent = (eventName: string, properties?: Record<string, any>) => {
  if (typeof window !== 'undefined' && posthog && posthog.__loaded) {
    posthog.capture(eventName, properties);
  }
};

export const identifyUser = (userId: string, properties?: Record<string, any>) => {
  if (typeof window !== 'undefined' && posthog) {
    posthog.identify(userId, properties);
  }
};

export const setUserProperties = (properties: Record<string, any>) => {
  if (typeof window !== 'undefined' && posthog) {
    posthog.people.set(properties);
  }
};

export const resetUser = () => {
  if (typeof window !== 'undefined' && posthog) {
    posthog.reset();
  }
};

// Fire a single `activated` milestone the first time a user performs a real
// value action (post, project, standup, comment, reaction). This is the
// activation event the onboarding funnel + Week-1 retention analysis need
// (LB-236). localStorage-guarded so it fires once per browser; a server-side
// dedup can be layered on later if cross-device precision matters.
const ACTIVATION_KEY = 'commons-activated';
export const markActivated = (firstAction: string) => {
  if (typeof window === 'undefined') return;
  try {
    if (localStorage.getItem(ACTIVATION_KEY)) return;
    localStorage.setItem(ACTIVATION_KEY, Date.now().toString());
    trackEvent('activated', { first_action: firstAction });
  } catch {
    // localStorage unavailable (private mode) — skip rather than risk spamming.
  }
};

// Track specific app events
export const trackAppEvents = {
  // Authentication events
  login: (method: 'magic_link' | 'google') => {
    trackEvent('user_logged_in', { method });
  },
  logout: () => {
    trackEvent('user_logged_out');
    resetUser();
  },
  signup: (method: string) => {
    trackEvent('user_signed_up', { method });
  },

  // Organization events
  createOrg: (orgName: string) => {
    trackEvent('organization_created', { org_name: orgName });
  },
  joinOrg: (orgId: string) => {
    trackEvent('organization_joined', { org_id: orgId });
  },
  switchOrg: (orgId: string) => {
    trackEvent('organization_switched', { org_id: orgId });
  },

  // Content creation events
  createPost: (type: string, hasImages: boolean) => {
    trackEvent('post_created', { type, has_images: hasImages });
    markActivated('post_created');
  },
  createProject: (projectName: string) => {
    trackEvent('project_created', { project_name: projectName });
    markActivated('project_created');
  },
  createTeam: (teamName: string) => {
    trackEvent('team_created', { team_name: teamName });
  },
  
  // Standup events
  submitStandup: (teamId: string, answersCount: number) => {
    trackEvent('standup_submitted', { team_id: teamId, answers_count: answersCount });
    markActivated('standup_submitted');
  },
  createStandupSession: (teamId: string) => {
    trackEvent('standup_session_created', { team_id: teamId });
  },
  closeStandupSession: (teamId: string) => {
    trackEvent('standup_session_closed', { team_id: teamId });
  },

  // Interaction events
  addReaction: (emoji: string, contentType: string) => {
    trackEvent('reaction_added', { emoji, content_type: contentType });
    markActivated('reaction_added');
  },
  addComment: (contentType: string) => {
    trackEvent('comment_added', { content_type: contentType });
    markActivated('comment_added');
  },
  uploadImage: (context: string, fileSize: number) => {
    trackEvent('image_uploaded', { context, file_size: fileSize });
  },

  // Poll events
  createPoll: (optionsCount: number) => {
    trackEvent('poll_created', { options_count: optionsCount });
  },
  votePoll: (pollId: string) => {
    trackEvent('poll_voted', { poll_id: pollId });
  },

  // Rollup events
  publishRollup: (projectCount: number, updateCount: number) => {
    trackEvent('rollup_published', { project_count: projectCount, update_count: updateCount });
  },

  // Retro events
  createRetroSession: (teamId: string | null, template: string) => {
    trackEvent('retro_session_created', { team_id: teamId, template });
  },
  submitRetroItem: (category: string) => {
    trackEvent('retro_item_submitted', { category });
  },
  voteRetroItem: () => {
    trackEvent('retro_item_voted');
  },

  // Navigation events
  viewFeed: () => {
    trackEvent('feed_viewed');
  },
  viewProject: (projectId: string) => {
    trackEvent('project_viewed', { project_id: projectId });
  },
  viewTeam: (teamId: string) => {
    trackEvent('team_viewed', { team_id: teamId });
  },

  // Sidebar navigation events
  sidebarNavClicked: (section: string, orgSlug: string) => {
    trackEvent('sidebar_nav_clicked', { section, org_slug: orgSlug });
  },
  sidebarOrgSwitched: (orgId: string, orgSlug: string) => {
    trackEvent('sidebar_org_switched', { org_id: orgId, org_slug: orgSlug });
  },
  sidebarPinnedToggled: (pinned: boolean) => {
    trackEvent('sidebar_pinned_toggled', { pinned });
  },
  sidebarSignedOut: () => {
    trackEvent('sidebar_signed_out');
  },
  sidebarFeedbackSubmitted: (feedbackType: string, orgSlug: string) => {
    trackEvent('sidebar_feedback_submitted', { feedback_type: feedbackType, org_slug: orgSlug });
  },
  sidebarCreateOrgClicked: () => {
    trackEvent('sidebar_create_org_clicked');
  },
  sidebarOrgSettingsClicked: (orgSlug: string) => {
    trackEvent('sidebar_org_settings_clicked', { org_slug: orgSlug });
  },
  sidebarAccountSettingsClicked: () => {
    trackEvent('sidebar_account_settings_clicked');
  },

  // Activity sidebar events
  activitySidebarActivityClicked: (action: string, targetType: string, projectId: string, orgSlug: string) => {
    trackEvent('activity_sidebar_activity_clicked', { action, target_type: targetType, project_id: projectId, org_slug: orgSlug });
  },

  // Project events
  viewProjectDetail: (projectId: string, projectName: string) => {
    trackEvent('project_detail_viewed', { project_id: projectId, project_name: projectName });
  },
  changeProjectStatus: (projectId: string, fromStatus: string, toStatus: string) => {
    trackEvent('project_status_changed', { project_id: projectId, from: fromStatus, to: toStatus });
  },
  importPrd: (projectId: string) => {
    trackEvent('prd_imported', { project_id: projectId });
  },

  // AI events
  aiSummaryGenerated: (feature: string) => {
    trackEvent('ai_summary_generated', { feature });
  },
  profileBriefingViewed: () => {
    trackEvent('profile_briefing_viewed');
  },

  // Profile + notifications adoption (LB-125)
  viewProfile: (isOwnProfile: boolean) => {
    trackEvent('profile_viewed', { is_own_profile: isOwnProfile });
  },
  viewNotifications: (unreadCount: number, surface: 'page' | 'dropdown' = 'page') => {
    trackEvent('notifications_viewed', { unread_count: unreadCount, surface });
  },

  // Workflow events
  createCritSession: (teamId: string | null) => {
    trackEvent('crit_session_created', { team_id: teamId });
  },
  createReview: () => {
    trackEvent('review_created');
  },

  // AI crit-prep adoption (LB-115)
  critPrepDrafted: (props: { mode: 'draft' | 'redraft'; focusArea?: string }) => {
    trackEvent('crit_prep_drafted', { mode: props.mode, focus_area: props.focusArea || null });
  },
  critPrepFailed: (reason: string) => {
    trackEvent('crit_prep_failed', { reason });
  },

  // Activation (LB-194): invite CTA surfaced from empty states.
  inviteTeamCtaClicked: (source: string) => {
    trackEvent('invite_team_cta_clicked', { source });
  },

  // Solo activation (LB-237): standalone "just for you" action from empty states.
  startSoloMindSweep: (source: string) => {
    trackEvent('solo_mind_sweep_started', { source });
  },

  // User-level workflows (LB-230).
  workflowActivated: (workflowType: string, scope: 'personal' | 'team', teamId?: string | null) => {
    trackEvent('workflow_activated', { workflow_type: workflowType, scope, team_id: teamId ?? null });
  },
  workflowDeactivated: (workflowType: string, scope: 'personal' | 'team') => {
    trackEvent('workflow_deactivated', { workflow_type: workflowType, scope });
  },
  workflowRan: (workflowType: string, scope: 'personal' | 'team', trigger: 'manual' | 'scheduled') => {
    trackEvent('workflow_ran', { workflow_type: workflowType, scope, trigger });
  },
};

/**
 * Properly capture an exception to PostHog with correct error properties.
 *
 * The PostHog SDK's `captureException` handles Error objects well via its
 * coercer chain, but many call sites pass `unknown` values (strings, objects,
 * undefined) cast as Error, and additional context using Sentry-style keys
 * (tags, extra, level) that PostHog ignores. This wrapper normalizes the
 * error into a real Error object and explicitly sets the `$exception_type`
 * and `$exception_message` properties that PostHog's Error Tracking UI
 * reads, preventing the null-type/null-message events we've been seeing.
 */
export const captureException = (
  error: unknown,
  additionalProperties?: Record<string, unknown>
) => {
  if (typeof window === 'undefined' || !posthog || !posthog.__loaded) {
    return;
  }

  // Normalize unknown values into a proper Error object
  const normalizedError = toError(error);

  // Flatten Sentry-style context (tags, extra) into flat properties
  const flatProps: Record<string, unknown> = {};
  if (additionalProperties) {
    for (const [key, value] of Object.entries(additionalProperties)) {
      if (key === 'tags' && typeof value === 'object' && value !== null) {
        // Flatten tags: { operation: 'x' } → { operation: 'x' }
        Object.assign(flatProps, value);
      } else if (key === 'extra' && typeof value === 'object' && value !== null) {
        // Flatten extra: { orgId: 'x' } → { orgId: 'x' }
        Object.assign(flatProps, value);
      } else if (key === 'level') {
        flatProps['$exception_level'] = value;
      } else {
        flatProps[key] = value;
      }
    }
  }

  // Set explicit exception properties so PostHog Error Tracking UI
  // always has type and message, even if the $exception_list coercion fails
  flatProps['$exception_type'] = normalizedError.name || 'Error';
  flatProps['$exception_message'] = normalizedError.message || 'Unknown error';

  posthog.captureException(normalizedError, flatProps);
};

/**
 * Convert an unknown thrown value into a proper Error object.
 * Handles strings, objects with message properties, undefined/null, etc.
 */
function toError(error: unknown): Error {
  if (error instanceof Error) {
    return error;
  }

  if (typeof error === 'string') {
    return new Error(error);
  }

  if (typeof error === 'object' && error !== null) {
    // Handle objects with a message property (e.g. { message: 'foo', code: 'bar' })
    const msg = (error as Record<string, unknown>).message;
    const name = (error as Record<string, unknown>).name;
    const err = new Error(typeof msg === 'string' ? msg : JSON.stringify(error));
    if (typeof name === 'string') {
      err.name = name;
    }
    return err;
  }

  return new Error(String(error ?? 'Unknown error'));
}

// Feature flags
export const isFeatureEnabled = (featureName: string): boolean => {
  if (typeof window !== 'undefined' && posthog) {
    return posthog.isFeatureEnabled(featureName) || false;
  }
  return false;
};

export const getFeatureFlag = (featureName: string): any => {
  if (typeof window !== 'undefined' && posthog) {
    return posthog.getFeatureFlag(featureName);
  }
  return null;
};

export default posthog;